<template>
  <div v-if="show" class="doc-levelling-backdrop" @click.self="$emit('close')">
    <div class="doc-levelling-popup" :class="{ 'doc-levelling-popup-expanded': showAll }" :style="details.length === 2 ? 'width: 50%;' : null">
      <!-- Toolbar -->
      <div class="doc-tab-strip">
        <div v-if="!showAll && details.length > 1" class="doc-tabs-scroll">
          <div
            v-for="(d, i) in details"
            :key="'dtab' + i"
            :class="['doc-tab', { 'doc-tab-active': i === activeIndex }]"
            :style="getTabStyle(d.document, i === activeIndex)"
            @click="activeIndex = i"
          >
            <span class="doc-tab-dot" :style="getDotStyle(d.document)"></span>
            <span class="doc-tab-name">{{ d.document.doc_name }}</span>
          </div>
        </div>
        <span v-else-if="showAll" class="doc-show-all-count">{{ details.length }} document{{ details.length > 1 ? 's' : '' }}</span>
        <span v-else class="doc-tab-spacer"></span>
        <div class="doc-tab-actions">
          <button class="doc-show-all-btn" @click="toggleShowAll" v-if="details.length > 1">
            <v-icon x-small class="mr-1">{{ showAll ? 'mdi-arrow-collapse' : 'mdi-view-column-outline' }}</v-icon>
            {{ showAll ? 'Single View' : 'Show All' }}
          </button>
          <v-icon small @click="$emit('close')" style="cursor: pointer;" class="ml-2">mdi-close</v-icon>
        </div>
      </div>

      <!-- Single document view -->
      <div v-if="!showAll && activeDetail" class="doc-single-view-content">
        <div class="doc-levelling-header">
          <div class="doc-levelling-title">
            <span class="doc-levelling-title-icon"><v-icon color="white" >mdi-briefcase-variant-outline</v-icon></span>
            {{ activeDetail.document.doc_name }}
          </div>
        </div>
        <div class="doc-levelling-body">
          <span class="doc-track" :style="getTrackColour(activeDetail.document[trackAttribute.target], false, true)">{{ activeDetail.document[trackAttribute.target] }}</span>
          <span class="doc-level">{{ levelAttribute.label }} {{ activeDetail.document[levelAttribute.target] }}</span>
          <span class="doc-levelling-status">
            <span v-if="activeDetail.document.lft_abbreviation" class="doc-levelling-lft">{{ activeDetail.document.lft_abbreviation }}</span>
            <span v-else class="doc-levelling-invalid">
              <v-icon small :color="activeDetail.document.lf_level_id_valid && activeDetail.document.lf_function_id_valid && activeDetail.document.lf_track_id_valid ? 'red' : 'amber'">mdi-alert</v-icon>
              {{ activeDetail.document.lft_status }}
            </span>
          </span>
          <div class="doc-hierarchy-container">
            <v-icon small>mdi-briefcase-variant-outline</v-icon>
            <span v-for="(h,hi) in linkHierarchyValues" :key="'h' + hi" class="doc-hierarchy">
              <span v-if="hi > 0" class="doc-hierarchy-separator">&bull;</span>{{ h }}
            </span>
          </div>
          <div v-if="busy" class="doc-levelling-loading">
            <v-progress-circular indeterminate size="30" color="grey"></v-progress-circular>
          </div>
          <template v-else-if="document">
            <div class="doc-related-heading">
                <v-icon color="#000000" class="mr-2" small>mdi-briefcase-variant-outline</v-icon>
              Role Description</div>
            <div v-for="(pt, pti) in document.partTypes.filter(x => partsVisible.some(pv => pv === x.pt_name))" :key="'pt' + pti" class="doc-levelling-part-type">
              <div v-for="(p, pi) in pt.parts" :key="'pt' + pti + 'p' + pi" :class="pt.partClass">
                <template v-if="p.usesAttributes">
                  <div v-for="(dpa, ai) in p.attributes" :key="'pt' + pti + 'p' + pi + 'a' + ai">
                    <span v-html="dpa.text" :class="{ ListPart: ai === 0 && (pt.moveable || pt.type === 'listitem') }"></span>
                  </div>
                </template>
                <template v-else>
                  <div v-for="(s, si) in p.sub_parts" :key="'pt' + pti + 'p' + pi + 's' + si">
                    <span v-html="s.text" :class="{ ListPart: pt.moveable || s.type === 'listitem' }"></span>
                  </div>
                </template>
              </div>
            </div>
          </template>
          <template v-if="activeDetail.related">
            <div class="doc-related-section" v-if="activeDetail.related.previous.length">
              <div class="doc-related-heading">
                  <v-icon color="#000000" class="mr-2" small>mdi-text-box-multiple-outline</v-icon>
                Typical Previous Roles</div>
              <div v-for="doc in activeDetail.related.previous" :key="doc.doc_id" class="doc-related-card" :style="getTrackColour(doc.LF_TRACK, false, true)">
                <span class="doc-levelling-linked-icon" :style="getTrackColour(doc.LF_TRACK, true)">
                  <v-icon class="doc-related-icon" color="white">mdi-briefcase-variant-outline</v-icon></span>
                <div class="doc-related-info">
                  <div class="doc-related-name">{{ doc.doc_name }}</div>
                  <div class="doc-related-meta">
                    {{ doc.LF_ABBREVIATION }} <span class="doc-related-sep">•</span> {{ doc.LF_TRACK }}
                  </div>
                </div>
              </div>
            </div>
            <div class="doc-related-section" v-if="activeDetail.related.current.length">
              <div class="doc-related-heading"><v-icon color="#000000" class="mr-2" small>mdi-text-box-multiple-outline</v-icon>Similar Roles</div>
              <div v-for="doc in activeDetail.related.current" :key="doc.doc_id" class="doc-related-card" :style="getTrackColour(doc.LF_TRACK, false, true)">
                <span class="doc-levelling-linked-icon" :style="getTrackColour(doc.LF_TRACK, true)">
                  <v-icon class="doc-related-icon" color="white">mdi-briefcase-variant-outline</v-icon></span>
                <div class="doc-related-info">
                  <div class="doc-related-name">{{ doc.doc_name }}</div>
                  <div class="doc-related-meta">
                    {{ doc.LF_ABBREVIATION }} <span class="doc-related-sep">•</span> {{ doc.LF_TRACK }}
                  </div>
                </div>
              </div>
            </div>
            <div class="doc-related-section" v-if="activeDetail.related.next.length">
              <div class="doc-related-heading"><v-icon color="#000000" class="mr-2" small>mdi-text-box-multiple-outline</v-icon>Career Progression</div>
              <div v-for="doc in activeDetail.related.next" :key="doc.doc_id" class="doc-related-card" :style="getTrackColour(doc.LF_TRACK, false, true)">
                <span class="doc-levelling-linked-icon" :style="getTrackColour(doc.LF_TRACK, true)">
                  <v-icon class="doc-related-icon" color="white">mdi-arrow-right-thick</v-icon></span>
                <div class="doc-related-info">
                  <div class="doc-related-name">{{ doc.doc_name }}</div>
                  <div class="doc-related-meta">
                    {{ doc.LF_ABBREVIATION }} <span class="doc-related-sep">•</span> {{ doc.LF_TRACK }}
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Show All: side-by-side columns -->
      <div v-if="showAll" class="doc-columns-container">
        <div v-for="(col, ci) in allColumnData" :key="'col' + ci" class="doc-column">
          <div class="doc-column-header" :style="getTabStyle(col.detail.document, true)">
            <div class="doc-column-title">
              <span class="doc-levelling-title-icon doc-levelling-title-icon-sm"><v-icon color="white" small>mdi-briefcase-variant-outline</v-icon></span>
              {{ col.detail.document.doc_name }}
            </div>
          </div>
          <div class="doc-column-body">
            <span class="doc-track" :style="getTrackColour(col.detail.document[trackAttribute.target], false, true)">{{ col.detail.document[trackAttribute.target] }}</span>
            <span class="doc-level">{{ levelAttribute.label }} {{ col.detail.document[levelAttribute.target] }}</span>
            <span class="doc-levelling-status">
              <span v-if="col.detail.document.lft_abbreviation" class="doc-levelling-lft">{{ col.detail.document.lft_abbreviation }}</span>
              <span v-else class="doc-levelling-invalid">
                <v-icon small :color="col.detail.document.lf_level_id_valid && col.detail.document.lf_function_id_valid && col.detail.document.lf_track_id_valid ? 'red' : 'amber'">mdi-alert</v-icon>
                {{ col.detail.document.lft_status }}
              </span>
            </span>
            <div class="doc-hierarchy-container">
              <v-icon small>mdi-briefcase-variant-outline</v-icon>
              <span v-for="(h,hi) in col.linkHierarchyValues" :key="'ch' + ci + 'h' + hi" class="doc-hierarchy">
                <span v-if="hi > 0" class="doc-hierarchy-separator">&bull;</span>{{ h }}
              </span>
            </div>
            <div v-if="col.busy" class="doc-levelling-loading">
              <v-progress-circular indeterminate size="30" color="grey"></v-progress-circular>
            </div>
            <template v-else-if="col.doc">
              <div class="doc-related-heading">
                  <v-icon color="#000000" class="mr-2" small>mdi-briefcase-variant-outline</v-icon>
                Role Description</div>
              <div v-for="(pt, pti) in col.doc.partTypes.filter(x => col.partsVisible.some(pv => pv === x.pt_name))" :key="'cpt' + ci + pti" class="doc-levelling-part-type">
                <div v-for="(p, pi) in pt.parts" :key="'cpt' + ci + pti + 'p' + pi" :class="pt.partClass">
                  <template v-if="p.usesAttributes">
                    <div v-for="(dpa, ai) in p.attributes" :key="'cpt' + ci + pti + 'p' + pi + 'a' + ai">
                      <span v-html="dpa.text" :class="{ ListPart: ai === 0 && (pt.moveable || pt.type === 'listitem') }"></span>
                    </div>
                  </template>
                  <template v-else>
                    <div v-for="(s, si) in p.sub_parts" :key="'cpt' + ci + pti + 'p' + pi + 's' + si">
                      <span v-html="s.text" :class="{ ListPart: pt.moveable || s.type === 'listitem' }"></span>
                    </div>
                  </template>
                </div>
              </div>
            </template>
            <template v-if="col.detail.related">
              <div class="doc-related-section" v-if="col.detail.related.previous.length">
                <div class="doc-related-heading">
                    <v-icon color="#000000" class="mr-2" small>mdi-text-box-multiple-outline</v-icon>
                  Typical Previous Roles</div>
                <div v-for="doc in col.detail.related.previous" :key="'cp' + ci + doc.doc_id" class="doc-related-card" :style="getTrackColour(doc.LF_TRACK, false, true)">
                  <span class="doc-levelling-linked-icon" :style="getTrackColour(doc.LF_TRACK, true)">
                    <v-icon class="doc-related-icon" color="white">mdi-briefcase-variant-outline</v-icon></span>
                  <div class="doc-related-info">
                    <div class="doc-related-name">{{ doc.doc_name }}</div>
                    <div class="doc-related-meta">
                      {{ doc.LF_ABBREVIATION }} <span class="doc-related-sep">•</span> {{ doc.LF_TRACK }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="doc-related-section" v-if="col.detail.related.current.length">
                <div class="doc-related-heading"><v-icon color="#000000" class="mr-2" small>mdi-text-box-multiple-outline</v-icon>Similar Roles</div>
                <div v-for="doc in col.detail.related.current" :key="'cc' + ci + doc.doc_id" class="doc-related-card" :style="getTrackColour(doc.LF_TRACK, false, true)">
                  <span class="doc-levelling-linked-icon" :style="getTrackColour(doc.LF_TRACK, true)">
                    <v-icon class="doc-related-icon" color="white">mdi-briefcase-variant-outline</v-icon></span>
                  <div class="doc-related-info">
                    <div class="doc-related-name">{{ doc.doc_name }}</div>
                    <div class="doc-related-meta">
                      {{ doc.LF_ABBREVIATION }} <span class="doc-related-sep">•</span> {{ doc.LF_TRACK }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="doc-related-section" v-if="col.detail.related.next.length">
                <div class="doc-related-heading"><v-icon color="#000000" class="mr-2" small>mdi-text-box-multiple-outline</v-icon>Career Progression</div>
                <div v-for="doc in col.detail.related.next" :key="'cn' + ci + doc.doc_id" class="doc-related-card" :style="getTrackColour(doc.LF_TRACK, false, true)">
                  <span class="doc-levelling-linked-icon" :style="getTrackColour(doc.LF_TRACK, true)">
                    <v-icon class="doc-related-icon" color="white">mdi-arrow-right-thick</v-icon></span>
                  <div class="doc-related-info">
                    <div class="doc-related-name">{{ doc.doc_name }}</div>
                    <div class="doc-related-meta">
                      {{ doc.LF_ABBREVIATION }} <span class="doc-related-sep">•</span> {{ doc.LF_TRACK }}
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
    <v-menu
      v-model="settingsDialog.show"
      absolute
      :position-x="settingsDialog.posX"
      :position-y="settingsDialog.posY"
      offset-x
      :close-on-content-click="false"
    >
      <v-card>
        <v-card-actions>
          Visible Sections:
          <v-spacer></v-spacer>
          <v-btn small outlined color="info" @click="showParts(true)">Show All</v-btn>
          <v-btn small outlined color="info" @click="showParts(false)">Hide All</v-btn>
          <v-btn color="blue darken-1" small outlined @click="saveSettings">Save</v-btn>
        </v-card-actions>
        <v-divider></v-divider>
        <v-card-text style="max-height: 400px; overflow: scroll" class="pt-1">
          <v-card color="basil" flat class="pt-1">
            <v-card-text class="py-1">
              <v-switch
                v-for="(pv, pti) in settingsDialog.partsVisible"
                :key="'pv' + pti"
                v-model="pv.visible"
                hide-details
                :label="pv.pt_name"
              ></v-switch>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, toRef, inject, set } from 'vue';
import utils from "@/common/utils.js";

export default {
  name: "DocLevellingPopUp",
  props: {
    show: { type: Boolean, default: false },
    details: { type: Array, default: () => [] },
    attributes: { type: Array, default: () => [] },
  },
  setup(props) {
    const store = inject('store');
    const loginState = inject('loginState');

    const busy = ref(false);
    const document = ref(null);
    const activeIndex = ref(0);
    const showAll = ref(false);
    const docCache = reactive({});
    const busyDocs = reactive({});
    const partsVisible = ref([]);
    const linkHierarchyValues = ref([]);
    const settingsDialog = reactive({
      show: false,
      posX: 0,
      posY: 0,
      partsVisible: [],
    });

    const trackAttribute = computed(() => props.attributes.find(x => x.name === 'TRACK') || {});
    const levelAttribute = computed(() => props.attributes.find(x => x.name === 'LEVEL') || {});
    const activeDetail = computed(() => props.details?.[activeIndex.value] || null);

    const getVisibleParts = (doc) => {
      if (!doc) return [];
      const settings = loginState.getSetting(
        `docLevellingViewSettings_${doc.doc_type}`,
        null
      );
      if (!settings?.partsVisible?.length) {
        return doc.partTypes
          .filter(p => p.pt_name.toLowerCase().indexOf("header") === -1 && (p.pt_name.toLowerCase().indexOf("purpose") > 0 || p.pt_name.toLowerCase() === "job usp"))
          .map(p => p.pt_name);
      }
      return doc.partTypes
        .filter(p => settings.partsVisible.some(pv => pv === p.pt_name))
        .map(p => p.pt_name);
    };

    const allColumnData = computed(() => {
      return props.details.map(d => {
        const docId = String(d.document.doc_id);
        const cached = docCache[docId];
        return {
          detail: d,
          busy: !!busyDocs[docId],
          doc: cached?.doc || null,
          linkHierarchyValues: cached?.linkHierarchyValues || [],
          partsVisible: cached?.doc ? getVisibleParts(cached.doc) : [],
        };
      });
    });

    const initDocument = () => {
      if (document.value) {
        partsVisible.value = getVisibleParts(document.value);
      }
    };

    const getTrackColour = (track, darken, pillStyle) => {
      if (!trackAttribute.value?.valueList) return;
      const val = trackAttribute.value.valueList.find(x => x.value === track);
      const colour = val?.background_colour || "#EEEEEE";
      if (val && pillStyle)
        return {
          backgroundColor: colour,
          borderColor: utils.darkenColor(colour, 15),
        }
      if (val)
        return {
          backgroundColor: darken ? utils.darkenColor(colour, 15) : colour,
        };
    };

    const getTabStyle = (doc, isActive) => {
      const track = doc[trackAttribute.value?.target];
      const val = trackAttribute.value?.valueList?.find(x => x.value === track);
      const colour = val?.background_colour || '#EEEEEE';
      if (isActive) {
        return {
          backgroundColor: colour,
          borderColor: utils.darkenColor(colour, 15),
        };
      }
      return {};
    };

    const getDotStyle = (doc) => {
      const track = doc[trackAttribute.value?.target];
      const val = trackAttribute.value?.valueList?.find(x => x.value === track);
      const colour = val?.background_colour || '#CCCCCC';
      return { backgroundColor: utils.darkenColor(colour, 10) };
    };

    const fetchDoc = (docId) => {
      return store
        .dispatch("docs/getDocumentSummary", docId)
        .then((resp) => {
          const docs = resp.data.Data.documents;
          const doc = docs[0];
          const lhValues = [];
          const lh = doc.hierarchies.find(x => x.linked_docs_default === 1);
          if (lh?.linklevel > 1) {
            for (let i = 2; i <= lh.linklevel; i++)
              lhValues.push(lh[`hierarchy${i}`]);
          }
          set(docCache, String(docId), { doc, linkHierarchyValues: lhValues });
          return { doc, linkHierarchyValues: lhValues };
        });
    };

    const loadActiveDoc = () => {
      const detail = activeDetail.value;
      if (!detail?.document?.doc_id) return;

      const docId = detail.document.doc_id;
      if (docCache[String(docId)]) {
        document.value = docCache[String(docId)].doc;
        linkHierarchyValues.value = docCache[String(docId)].linkHierarchyValues;
        initDocument();
        return;
      }

      busy.value = true;
      document.value = null;
      linkHierarchyValues.value = [];

      fetchDoc(docId)
        .then(({ doc, linkHierarchyValues: lhValues }) => {
          document.value = doc;
          linkHierarchyValues.value = lhValues;
          initDocument();
          busy.value = false;
        })
        .catch((error) => {
          console.error(error);
          busy.value = false;
        });
    };

    const loadAllDocs = () => {
      props.details.forEach(d => {
        const docId = d.document?.doc_id;
        if (!docId || docCache[String(docId)]) return;
        set(busyDocs, String(docId), true);
        fetchDoc(docId)
          .then(() => { set(busyDocs, String(docId), false); })
          .catch(() => { set(busyDocs, String(docId), false); });
      });      
    };

    const toggleShowAll = () => {
      showAll.value = !showAll.value;
      if (showAll.value) {
        loadAllDocs();
      }
    };

    const showSettings = (event) => {
      settingsDialog.show = true;
      settingsDialog.posX = event.clientX;
      settingsDialog.posY = event.clientY;
      settingsDialog.partsVisible = document.value.partTypes.map((p) => ({
        pt_name: p.pt_name,
        visible: partsVisible.value.some((pv) => pv === p.pt_name),
      }));
    };

    const showParts = (show) => {
      settingsDialog.partsVisible.forEach((x) => (x.visible = show));
    };

    const saveSettings = () => {
      settingsDialog.show = false;
      partsVisible.value = settingsDialog.partsVisible
        .filter((x) => x.visible)
        .map((x) => x.pt_name);
      loginState.saveSetting(
        `docLevellingViewSettings_${document.value.doc_type}`,
        {
          partsVisible: partsVisible.value,
        }
      );
    };

    watch(toRef(props, 'show'), (val) => {
      if (val && props.details?.length) {
        activeIndex.value = 0;
        showAll.value = false;
        if (props.details.length > 1) 
          toggleShowAll();
        else 
          loadActiveDoc();
      }
      if (!val) {
        document.value = null;
        activeIndex.value = 0;
        showAll.value = false;
        Object.keys(docCache).forEach(k => delete docCache[k]);
        Object.keys(busyDocs).forEach(k => delete busyDocs[k]);
      }
    });

    watch(activeIndex, () => {
      if (props.show && !showAll.value && activeDetail.value?.document?.doc_id) {
        loadActiveDoc();
      }
    });

    return {
      busy,
      document,
      activeIndex,
      activeDetail,
      showAll,
      allColumnData,
      partsVisible,
      settingsDialog,
      linkHierarchyValues,
      trackAttribute,
      levelAttribute,
      toggleShowAll,
      showSettings,
      showParts,
      saveSettings,
      getTrackColour,
      getTabStyle,
      getDotStyle,
    };
  },
};
</script>

<style lang="scss" scoped>
.doc-levelling-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
}

.doc-levelling-popup {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 101;
  background: #FFFFFF;
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.12);
  border-left: 1px solid #E0E0E0;
  width: 25%;
  height: 100vh;
  overflow-y: auto;
  font-family: 'DM Sans', sans-serif;
  padding: 16px 0 16px 16px;
  transition: width 0.3s ease;
  display: flex;
  flex-direction: column;
}

.doc-levelling-popup-expanded {
  width: 80%;
  overflow-y: hidden;
}

.doc-single-view-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.doc-levelling-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px;
  // margin-bottom: 12px;
}

.doc-levelling-title {
  display: flex;
  align-items: center;
  font-size: 24px;
  font-weight: 400;
  color: #0A0A0A !important;
  font-family: 'DM Serif Display' !important;
  line-height: 1;
  letter-spacing: 0;
  word-break: break-word;
}
.doc-levelling-title-icon {
  flex-shrink: 0;
  background-color: #50B3CB;
  border-radius: 12px;
  padding: 12px;
  margin-right: 12px;
}
.doc-levelling-title-icon-sm {
  padding: 0 8px;
  margin-right: 8px;
}
.doc-levelling-linked-icon {
  background-color: #dddddd;
  border-radius: 10px;
  padding: 4px;
}
.doc-levelling-sysnum {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  color: #5C6BC0;
  background: #E8EAF6;
  border-radius: 4px;
  padding: 1px 6px;
  margin-right: 6px;
  vertical-align: middle;
}

.doc-levelling-body {
  padding: 12px 16px;
}

.doc-levelling-status {
  margin-bottom: 12px;
}

.doc-levelling-lft {
  background: #EEEEEE;
  border-radius: 4px;
  padding: 2px 8px;
}

.doc-levelling-invalid {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #999;
}
.doc-track {
  border: solid 1px #50B3CB;
  background-color: #EEF7F9;
  border-radius: 4px;
  padding: 4px 8px;
}
.doc-level, .doc-track, .doc-levelling-lft {
  font-size: 14px;
  font-weight: 400;
  color: #000000;
  font-family: 'DM Sans' !important;
  letter-spacing: 0;
}
.doc-level {
  margin: 0 8px;
}
.doc-hierarchy {
  font-size: 11px;
  font-weight: 400;
  color: #888888;
  font-family: 'DM Sans' !important;
  letter-spacing: 0;
  padding-left: 8px;
}
.doc-hierarchy-container {
  margin-top: 12px;
  margin-bottom: 12px;
}
.doc-hierarchy-separator {
  font-size: 20px;
  padding-right: 8px;
}

.doc-levelling-attrs {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.doc-levelling-attr {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
}

.doc-levelling-attr-label {
  font-size: 12px;
  font-weight: 600;
  color: #888;
  white-space: nowrap;
}

.doc-levelling-attr-value {
  font-size: 13px;
  color: #333;
  text-align: right;
  word-break: break-word;
}

.doc-levelling-loading {
  display: flex;
  justify-content: center;
  padding: 16px 0;
}

.doc-levelling-part-type {
  margin-top: 12px;
  font-size: 13px;
  color: #333;
  line-height: 1.5;
}

.doc-related-section {
  margin-top: 16px;
}

.doc-related-heading {
  font-size: 18px;
  font-weight: 700;
  font-family: 'DM Sans' !important;
  color: #000000 !important;
  letter-spacing: 0;
  margin-bottom: 8px;
}

.doc-related-card {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #EEEEEE;
  border-radius: 10px;
  border: 1px solid #dddddd;
  padding: 10px 12px;
  margin-bottom: 6px;
}

.doc-related-icon {
  font-size: 20px !important;
  border-radius: 6px;
  padding: 6px;
}

.doc-related-info {
  min-width: 0;
}

.doc-related-name {
  font-size: 16px;
  font-weight: 700;
  font-family: 'DM Sans' !important;
  color: #000000 !important;
  letter-spacing: 0;
  line-height: 1;
  word-break: break-word;
}

.doc-related-meta {
  font-size: 14px;
  font-weight: 400;
  font-family: 'DM Sans' !important;
  color: #000000 !important;
  letter-spacing: 0;
  margin-top: 2px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
}

.doc-related-sep {
  margin: 0 2px;
  color: #bbb;
}

.doc-related-badge {
  font-size: 10px;
  font-weight: 600;
  border-radius: 4px;
  padding: 1px 6px;

  &.lateral {
    background: #E8EAF6;
    color: #5C6BC0;
  }

  &.previous {
    background: #FFF3E0;
    color: #E67E22;
  }

  &.progression {
    background: #E8F5E9;
    color: #43A047;
  }
}

/* Multi-document tab strip */
.doc-tab-strip {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid #EEEEEE;
  margin-bottom: 4px;
  overflow: hidden;
}

.doc-tabs-scroll {
  display: flex;
  flex: 1;
  min-width: 0;
  gap: 6px;
  overflow-x: auto;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    height: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }
}

.doc-tab {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 24px;
  border: 1.5px solid #E0E0E0;
  background: #F5F5F5;
  cursor: pointer;
  transition: all 0.2s ease;
  max-width: 220px;

  &:hover:not(.doc-tab-active) {
    background: #EEEEEE;
    transform: translateY(-1px);
  }
}

.doc-tab-active {
  border-width: 1.5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  font-weight: 600;
}

.doc-tab-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.doc-tab-name {
  font-size: 13px;
  font-family: 'DM Sans', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}

.doc-tab-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.doc-show-all-btn {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  font-size: 12px;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  color: #333;
  background: #F5F5F5;
  border: 1px solid #E0E0E0;
  border-radius: 16px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;

  &:hover {
    background: #EEEEEE;
  }
}

.doc-tab-spacer {
  flex: 1;
}

.doc-show-all-count {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  color: #666;
  font-family: 'DM Sans', sans-serif;
}

/* Show All: side-by-side columns */
.doc-columns-container {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow-x: auto;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }
}

.doc-column {
  flex: 1 0 25%;
  min-width: 280px;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #E0E0E0;

  &:last-child {
    border-right: none;
  }
}

.doc-column-header {
  flex-shrink: 0;
  padding: 12px 16px;
  border-bottom: 1px solid #EEEEEE;
}

.doc-column-title {
  font-size: 18px;
  font-weight: 400;
  color: #0A0A0A !important;
  font-family: 'DM Serif Display' !important;
  line-height: 1.2;
  word-break: break-word;
}

.doc-column-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
}
</style>
